import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FaQuestionCircle, FaEdit, FaUpload, FaTimes } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { listingService } from '../services/listingService';
import toast from 'react-hot-toast';
import { locationData, provinces } from '../utils/locationData';
import ConfirmDialog from '../components/ConfirmDialog';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  background-color: ${props => props.theme.colors.background};
  min-height: calc(100vh - 200px);
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const FormCard = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 10px;
  padding: 2rem;
  box-shadow: 0 2px 10px ${props => props.theme.colors.shadow};
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const Title = styled.h1`
  color: #FF6B6B;
  text-align: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.text};
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 5px;
  font-size: 1rem;
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  
  &:focus {
    outline: none;
    border-color: #FF6B6B;
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.65rem;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 5px;
  font-size: 1rem;
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  min-height: 120px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #FF6B6B;
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.65rem;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 5px;
  font-size: 1rem;
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  
  &:focus {
    outline: none;
    border-color: #FF6B6B;
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.65rem;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 5px;
  background-color: ${props => props.theme.colors.surface};
  max-height: 200px;
  overflow-y: auto;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: ${props => props.theme.colors.text};
  
  input[type="checkbox"] {
    cursor: pointer;
    width: 18px;
    height: 18px;
  }
  
  &:hover {
    color: #FF6B6B;
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const DatesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const DateChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #FF6B6B;
  color: white;
  border-radius: 20px;
  font-size: 0.9rem;
  
  button {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 0;
    font-size: 1.2rem;
    line-height: 1;
    
    &:hover {
      color: #FFE6E6;
    }
  }
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 0.4rem 0.75rem;
  }
`;

const PostButton = styled.button`
  background: linear-gradient(135deg, #FF6B6B, #FF8E53);
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 25px;
  cursor: pointer;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const LoginPrompt = styled.div`
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  padding: 2rem;
  border-radius: 10px;
  text-align: center;
  margin-bottom: 2rem;
  box-shadow: 0 2px 10px ${props => props.theme.colors.shadow};
  
  h2 {
    color: ${props => props.theme.colors.text};
  }
  
  p {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const LoginButton = styled.button`
  background: linear-gradient(135deg, #FF6B6B, #FF8E53);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 20px;
  cursor: pointer;
  margin-left: 1rem;
`;

const ShareGig = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const editListing = location.state?.editListing;
  const isEditing = !!editListing;
  
  const [formData, setFormData] = useState({
    gigDescription: '',
    location: { province: '', area: '', customArea: '' },
    traveling: '',
    fullStory: '',
    remote: '',
    skills: [],
    timeCommitment: '',
    feeStructure: '',
    feeRange: '',
    dates: [] // Changed from single date to array of dates
  });
  
  const [availableAreas, setAvailableAreas] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Load edit data if editing
  useEffect(() => {
    if (editListing) {
      const location = editListing.location || { province: '', area: '', customArea: '' };
      
      // Parse fee structure and range from filters or pay field
      let feeStructure = '';
      let feeRange = '';
      
      // Try to get from filters first
      if (editListing.filters?.fee_structure) {
        feeStructure = editListing.filters.fee_structure;
      }
      if (editListing.filters?.fee_range) {
        feeRange = editListing.filters.fee_range;
      }
      
      // If not in filters, try to parse from pay field (format: "hourly (R100-R200)")
      if (!feeStructure && editListing.pay) {
        const payMatch = editListing.pay.match(/^([^(]+)/);
        if (payMatch) {
          feeStructure = payMatch[1].trim();
        }
      }
      if (!feeRange && editListing.pay) {
        const rangeMatch = editListing.pay.match(/\(([^)]+)\)/);
        if (rangeMatch) {
          feeRange = rangeMatch[1].trim();
        }
      }
      
      setFormData({
        gigDescription: editListing.title || '',
        location: location,
        traveling: editListing.traveling ? 'yes' : 'no',
        fullStory: editListing.full_story || '',
        remote: editListing.remote || '',
        skills: editListing.skills || [],
        timeCommitment: editListing.time_commitment || '',
        feeStructure: feeStructure,
        feeRange: feeRange,
        dates: editListing.filters?.dates || []
      });
      // Set available areas if province is set
      if (location.province) {
        setAvailableAreas(locationData[location.province] || []);
      }
    }
  }, [editListing]);

  if (!user) {
    return (
      <Container>
        <LoginPrompt>
          <h2>Login Required</h2>
          <p>You need to be logged in to post gig opportunities.</p>
          <LoginButton onClick={() => navigate('/login')}>
            Sign In / Register
          </LoginButton>
        </LoginPrompt>
      </Container>
    );
  }

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      
      // Handle province change - reset area and update available areas
      if (field === 'location.province') {
        setFormData(prev => ({
          ...prev,
          location: { ...prev.location, province: value, area: '', customArea: '' }
        }));
        setAvailableAreas(value && value !== 'Anywhere' ? (locationData[value] || []) : []);
      } 
      // Handle area change - clear custom area if not "Other"
      else if (field === 'location.area') {
        setFormData(prev => ({
          ...prev,
          location: { ...prev.location, area: value, customArea: value !== 'Other' ? '' : prev.location.customArea }
        }));
      }
      else {
        setFormData(prev => ({
          ...prev,
          [parent]: { ...prev[parent], [child]: value }
        }));
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSkillToggle = (skill) => {
    setFormData(prev => {
      const skills = prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill];
      return { ...prev, skills };
    });
  };

  const handleDateAdd = (date) => {
    if (!date) return;
    if (formData.dates.includes(date)) {
      toast.error('This date has already been added');
      return;
    }
    setFormData(prev => ({
      ...prev,
      dates: [...prev.dates, date].sort()
    }));
  };

  const handleDateRemove = (date) => {
    setFormData(prev => ({
      ...prev,
      dates: prev.dates.filter(d => d !== date)
    }));
  };

  const handlePost = async () => {
    if (!formData.gigDescription || !formData.fullStory) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.dates.length === 0) {
      toast.error('Please add at least one date for your gig');
      return;
    }

    if (!user || !profile) {
      toast.error('You must be logged in to create a post');
      return;
    }

    setShowConfirmDialog(true);
  };

  const handleConfirm = async () => {
    setShowConfirmDialog(false);
    setLoading(true);

    try {
      // Combine fee structure and range for display
      const payDisplay = formData.feeStructure && formData.feeRange
        ? `${formData.feeStructure} (${formData.feeRange})`
        : formData.feeStructure || formData.feeRange || '';

      const listingData = {
        type: 'gig',
        title: formData.gigDescription,
        short_description: formData.gigDescription,
        description: formData.gigDescription,
        full_story: formData.fullStory,
        location: formData.location,
        traveling: formData.traveling === 'yes',
        remote: formData.remote,
        skills: formData.skills,
        time_commitment: formData.timeCommitment,
        pay: payDisplay,
        filters: {
          dates: formData.dates,
          fee_range: formData.feeRange,
          fee_structure: formData.feeStructure,
          location: formData.location,
          traveling: formData.traveling === 'yes',
          remote: formData.remote,
          skills: formData.skills,
          time_commitment: formData.timeCommitment,
          pay: payDisplay
        }
      };

      if (isEditing) {
        // Update existing listing and send back to moderation
        await listingService.updateListing(editListing.id, {
          ...listingData,
          status: 'pending_moderation',
          submitted_at: new Date().toISOString()
        });
        toast.success(
          'Gig updated successfully! Your changes have been submitted for review. ' +
          'Our team will validate and approve your post within 2 business days.'
        );
        navigate('/profile');
      } else {
        // Create new listing
        listingData.owner_id = user.id;
        listingData.status = 'draft';
        
        const createdListing = await listingService.createListing(listingData);
        await listingService.submitForModeration(createdListing.id);

        toast.success(
          'Congratulations! Your gig has been submitted for review. ' +
          'Our team will validate and approve your post within 2 business days. ' +
          'You will receive a notification once it is approved.'
        );
        
        navigate('/find-gig');
      }
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} gig:`, error);
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} gig: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <FormCard>
        <Title>{isEditing ? 'Edit your Gig' : 'Share your Gig'}</Title>
        <p style={{ textAlign: 'center', color: '#666', marginBottom: '2rem' }}>
          {isEditing 
            ? 'Update your gig details below'
            : 'Tell your story, describe your gig, and outline the skills you\'re seeking to help your business grow'
          }
        </p>

        <FormGroup>
          <Label>Short gig description</Label>
          <Input
            placeholder="One line summary of your gig..."
            value={formData.gigDescription}
            onChange={(e) => handleInputChange('gigDescription', e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Location</Label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <Select
              value={formData.location.province}
              onChange={(e) => handleInputChange('location.province', e.target.value)}
            >
              <option value="">Select Province</option>
              <option value="Anywhere">Anywhere (Remote)</option>
              {provinces.map(province => (
                <option key={province} value={province}>{province}</option>
              ))}
            </Select>
            <Select
              value={formData.location.area}
              onChange={(e) => handleInputChange('location.area', e.target.value)}
              disabled={!formData.location.province || formData.location.province === 'Anywhere'}
            >
              <option value="">Select Area</option>
              {availableAreas.map(area => (
                <option key={area} value={area}>{area}</option>
              ))}
              <option value="Other">Other</option>
            </Select>
          </div>
          {formData.location.area === 'Other' && (
            <Input
              placeholder="Enter your area/city"
              value={formData.location.customArea}
              onChange={(e) => handleInputChange('location.customArea', e.target.value)}
              style={{ marginTop: '0.5rem' }}
            />
          )}
        </FormGroup>

        <FormGroup>
          <Label>Traveling</Label>
          <Select
            value={formData.traveling}
            onChange={(e) => handleInputChange('traveling', e.target.value)}
          >
            <option value="">Select...</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Full story (Here you can give the full story of your gig. What you need from someone. What they need to wear. The roles and tasks.)</Label>
          <TextArea
            style={{ minHeight: '150px' }}
            placeholder="Describe what the gig involves, requirements, and expectations..."
            value={formData.fullStory}
            onChange={(e) => handleInputChange('fullStory', e.target.value)}
          />
        </FormGroup>

        <FormGroup>
          <Label>Work Structure</Label>
          <Select
            value={formData.remote}
            onChange={(e) => handleInputChange('remote', e.target.value)}
          >
            <option value="">Select work structure...</option>
            <option value="on-site">On-site</option>
            <option value="hybrid">Hybrid</option>
            <option value="remote">Remote</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Skills (Select all that apply)</Label>
          <CheckboxGroup>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={formData.skills.includes('customer-service')}
                onChange={() => handleSkillToggle('customer-service')}
              />
              Customer Service
            </CheckboxLabel>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={formData.skills.includes('hospitality')}
                onChange={() => handleSkillToggle('hospitality')}
              />
              Hospitality
            </CheckboxLabel>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={formData.skills.includes('photography')}
                onChange={() => handleSkillToggle('photography')}
              />
              Photography
            </CheckboxLabel>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={formData.skills.includes('technical')}
                onChange={() => handleSkillToggle('technical')}
              />
              Technical Support
            </CheckboxLabel>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={formData.skills.includes('creative')}
                onChange={() => handleSkillToggle('creative')}
              />
              Creative Arts
            </CheckboxLabel>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={formData.skills.includes('marketing')}
                onChange={() => handleSkillToggle('marketing')}
              />
              Marketing
            </CheckboxLabel>
          </CheckboxGroup>
        </FormGroup>

        <FormGroup>
          <Label>Time commitment</Label>
          <Select
            value={formData.timeCommitment}
            onChange={(e) => handleInputChange('timeCommitment', e.target.value)}
          >
            <option value="">Select time commitment...</option>
            <option value="1-4">1-4 hours</option>
            <option value="4-8">4-8 hours</option>
            <option value="8+">8+ hours</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Fee Structure</Label>
          <Select
            value={formData.feeStructure}
            onChange={(e) => handleInputChange('feeStructure', e.target.value)}
          >
            <option value="">Select payment structure...</option>
            <option value="hourly">Hourly</option>
            <option value="daily">Daily</option>
            <option value="per-gig">Per Gig</option>
            <option value="commission">Commission</option>
            <option value="other">Other</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Fee Range (Optional)</Label>
          <Select
            value={formData.feeRange}
            onChange={(e) => handleInputChange('feeRange', e.target.value)}
          >
            <option value="">Select fee range...</option>
            <option value="<R50">&lt;R50</option>
            <option value="R50-R100">R50 - R100</option>
            <option value="R100-R200">R100 - R200</option>
            <option value="R200-R300">R200 - R300</option>
            <option value="R300-R500">R300 - R500</option>
            <option value="R500+">R500+</option>
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Dates (Add multiple dates for your gig)</Label>
          <Input
            type="date"
            min={new Date().toISOString().split('T')[0]}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleDateAdd(e.target.value);
                e.target.value = '';
              }
            }}
            onBlur={(e) => {
              if (e.target.value) {
                handleDateAdd(e.target.value);
                e.target.value = '';
              }
            }}
          />
          {formData.dates.length > 0 && (
            <DatesContainer>
              {formData.dates.map(date => (
                <DateChip key={date}>
                  {new Date(date).toLocaleDateString('en-ZA', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                  <button 
                    type="button" 
                    onClick={() => handleDateRemove(date)}
                    title="Remove date"
                  >
                    <FaTimes />
                  </button>
                </DateChip>
              ))}
            </DatesContainer>
          )}
        </FormGroup>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem', gap: '1rem' }}>
          {isEditing && (
            <PostButton 
              style={{ background: '#6c757d' }} 
              onClick={() => navigate('/profile')}
              disabled={loading}
            >
              <FaTimes />
              CANCEL
            </PostButton>
          )}
          <PostButton onClick={handlePost} disabled={loading}>
            <FaEdit />
            {loading ? (isEditing ? 'UPDATING...' : 'SUBMITTING...') : (isEditing ? 'UPDATE' : 'POST')}
          </PostButton>
        </div>
      </FormCard>

      {showConfirmDialog && (
        <ConfirmDialog
          message={isEditing ? 'Are you sure you want to save these changes?' : 'Are you sure you are satisfied with the gig post?'}
          onConfirm={handleConfirm}
          onCancel={() => setShowConfirmDialog(false)}
          confirmText="OK"
          cancelText="Cancel"
          primaryColor="#FF6B6B"
        />
      )}
    </Container>
  );
};

export default ShareGig;
